import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import {
  withTrackProps,
  createLogEntryPayload,
} from '@dpdgroupuk/react-event-tracker';
import { pick } from 'ramda';

import { isRouteStatusInBuild } from '../../../store/selectors/route';
import {
  getSelectedParcelsCount,
  getSelectedPreparedStops,
  getStopFilters,
  getParcelTransferDisabledState,
  isKingColumnDisabled,
} from '../../../store/selectors/ui/routeDetail';
import * as routeDetailActions from '../../../store/actions/ui/routeDetail';
import {
  onChangeBulkStop,
  updateKingParcel,
} from '../../../store/actions/stop';
import useParcelDetailsTableProps from '../hooks/useParcelDetailsTableProps';
import RouteManagement from '../../../components/RouteManagement';
import SplitStopModal from '../../../components/modals/SplitStop';
import ModalControlled from '../../../components/ModalControlled';
import { wrapPromise } from '../../../utils/promise';
import { showNotification } from '../../../store/actions/notifications';
import { NotificationType } from '../../Notifier';
import SelectByCriteriaModal from '../../selectByCriteria';
import {
  useBodyParcelDetailsCellStyles,
  useHeaderParcelDetailsCellStyles,
} from '../../../components/styles/tablesCells';
import useSplitStopModalData from '../hooks/useSplitStopModalData';
import { DISMISS } from '../../../constants/strings';
import { isBulkDisabled, isSplitDisabled } from '../../../models/routeDetail';
import modalTypes from '../../../enums/modalTypes';
import {
  ROUTE_MANAGEMENT,
  STOP_PARCELS_LIST,
} from '../../../constants/analytics';
import { useOverlay } from '../../../components/Overlay';
import useRouteManagementActionsConfig from '../hooks/useRouteManagementActionsConfig';
import { getBulkStopThreshold } from '../../../store/selectors/app';
import { resetSelectedParcels } from '../../../store/reducers/ui/routeDetail/selectedParcels';

const RouteManagementContainer = ({
  handleBackClick,
  onParcelRequestClick,
  selectedCount,
  selectedStops,
  isKingDisabled,
  criteria,
  isParcelTransferDisabled,
  routeStatusInBuild,
  handleParcelSelect,
  onChangeBulk,
  onRemoveCriteria,
  showNotificationMessage,
  onUpdateKingParcel,
  onSplitClickActionId,
  onClickAddNewCriteriaActionId,
  onParcelNumberClick,
  handleOnLoad,
  bulkStopThreshold,
}) => {
  const selectByCriteriaModalRef = useRef();

  const { showOverlayWhile } = useOverlay();

  const routeManagementActionButtonsConfig = useRouteManagementActionsConfig();

  const parcelDetailsTableProps = useParcelDetailsTableProps({
    modifiers: {
      onUpdateKingParcel,
      onParcelRequestClick,
      onParcelNumberClick,
    },
    headerCellClasses: useHeaderParcelDetailsCellStyles(),
    bodyCellClasses: useBodyParcelDetailsCellStyles(),
    isKingParcelColumnDisabled: isKingDisabled,
  });

  useEffect(() => {
    handleOnLoad();
  }, [handleOnLoad]);

  useEffect(() => {
    if (criteria.length && !selectedStops.length) {
      showNotificationMessage();
    }
  }, [criteria, showNotificationMessage, selectedStops.length]);

  const { splitStopModalRef, openSplitStopsModal } = useSplitStopModalData();

  const handleOnSplitClick = useCallback(() => {
    onSplitClickActionId();
    openSplitStopsModal();
  }, [onSplitClickActionId, openSplitStopsModal]);

  const handleSelectByCriteria = useCallback(() => {
    onClickAddNewCriteriaActionId();
    wrapPromise(selectByCriteriaModalRef.current.showModal());
  }, [onClickAddNewCriteriaActionId]);

  const splitDisabled = useMemo(
    () => isSplitDisabled(selectedStops, routeStatusInBuild, selectedCount),
    [selectedStops, routeStatusInBuild, selectedCount]
  );

  const bulkDisabled = useMemo(
    () => isBulkDisabled(selectedStops, bulkStopThreshold, routeStatusInBuild),
    [bulkStopThreshold, routeStatusInBuild, selectedStops]
  );

  return (
    <>
      <RouteManagement
        tableProps={parcelDetailsTableProps}
        selectedStops={selectedStops}
        handleRowClick={handleParcelSelect}
        handleBackClick={showOverlayWhile(handleBackClick)}
        isBulkDisabled={bulkDisabled}
        isSplitDisabled={splitDisabled}
        isTransferDisabled={isParcelTransferDisabled}
        onChangeBulk={showOverlayWhile(onChangeBulk)}
        onSplitClick={showOverlayWhile(handleOnSplitClick)}
        selectedCriteria={criteria}
        onClickAddNewCriteria={showOverlayWhile(handleSelectByCriteria)}
        onRemoveCriteria={onRemoveCriteria}
        routeManagementActionButtonsConfig={routeManagementActionButtonsConfig}
      />
      <ModalControlled
        ref={splitStopModalRef}
        Component={SplitStopModal}
        renderWhenOpen
        type={modalTypes.RIGHT_DRAWER}
      />
      <ModalControlled
        ref={selectByCriteriaModalRef}
        Component={SelectByCriteriaModal}
        renderWhenOpen
        type={modalTypes.RIGHT_DRAWER}
      />
    </>
  );
};
const mapStateToProps = (state) => {
  const { criteria } = getStopFilters(state);
  return {
    selectedCount: getSelectedParcelsCount(state),
    selectedStops: getSelectedPreparedStops(state),
    isKingDisabled: isKingColumnDisabled(state),
    criteria,
    isParcelTransferDisabled: getParcelTransferDisabledState(state),
    routeStatusInBuild: isRouteStatusInBuild(state),
    bulkStopThreshold: getBulkStopThreshold(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  ...bindActionCreators(
    pick(
      ['handleParcelSelect', 'setCriteria', 'removeCriteria'],
      routeDetailActions
    ),
    dispatch
  ),
  onChangeBulk: () => dispatch(onChangeBulkStop()),
  onRemoveCriteria: (id) => {
    dispatch(resetSelectedParcels());
    dispatch(routeDetailActions.removeCriteria(id));
  },
  showNotificationMessage: () =>
    dispatch(
      showNotification({
        key: 'no-stops-by-criteria-message',
        type: NotificationType.ACTION,
        message: 'No stops match the selected criteria',
        buttonText: DISMISS,
      })
    ),
  onUpdateKingParcel: (value) => dispatch(updateKingParcel(value)),
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withTrackProps({
    handleParcelSelect: (value) =>
      createLogEntryPayload(STOP_PARCELS_LIST.SELECT_PARCEL_ROW, { value }),
    onUpdateKingParcel: STOP_PARCELS_LIST.SELECT_KING_PARCEL,
    onChangeBulk: STOP_PARCELS_LIST.SELECT_BULK,
    onParcelRequestClick: (value) =>
      createLogEntryPayload(STOP_PARCELS_LIST.CLICK_REQUEST, { value }),
    onSplitClickActionId: STOP_PARCELS_LIST.CLICK_SPLIT,
    onParcelNumberClick: (value) =>
      createLogEntryPayload(STOP_PARCELS_LIST.CLICK_PARCEL_NUMBER, { value }),
    onClickAddNewCriteriaActionId: ROUTE_MANAGEMENT.CLICK_ADD_NEW_CRITERIA,
    handleBackClick: ROUTE_MANAGEMENT.CLICK_BACK,
    onRemoveCriteria: (value) =>
      createLogEntryPayload(ROUTE_MANAGEMENT.CLICK_REMOVE_CRITERIA, { value }),
    handleOnLoad: ROUTE_MANAGEMENT.LOAD,
  })
)(RouteManagementContainer);
